import { useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'mp.theme';
const LIGHT_CLASS = 'theme-light';

function readStored(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const value = localStorage.getItem(STORAGE_KEY);
  return value === 'light' ? 'light' : 'dark';
}

function apply(theme: Theme) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (theme === 'light') html.classList.add(LIGHT_CLASS);
  else                   html.classList.remove(LIGHT_CLASS);
}

/**
 * useTheme — global day / night toggle.
 * Persists across reloads in localStorage and applies a class to <html>
 * which the CSS overrides in index.css hook into.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => readStored());

  // Apply on every change AND on mount (initial paint)
  useEffect(() => {
    apply(theme);
  }, [theme]);

  const setTheme = (next: Theme) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  };

  const toggle = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  return { theme, setTheme, toggle, isLight: theme === 'light' };
}
